import { Component, OnInit, OnDestroy, ViewChild } from "@angular/core";
import { Location } from "@angular/common";
import { Router, NavigationEnd, NavigationStart, ActivatedRoute } from "@angular/router";
import { ProgramEditorService } from "../../common/program-editors/program-editor.service";
import { ProgramService } from "../program.service";
import { MessageService } from "primeng/components/common/messageservice";
import { TypesService } from "../../common/types.service";

import { Subscription } from "rxjs/Subscription";

import { Message } from "primeng/primeng";
import {
    Program,
    ProgramContactInfo,
    ProgramDetails,
    ProgramFunding,
    ProgramServices,
    ProgramTimings,
    ProgramGradesServed,
    ProgramProgramsOffered,
    ProgramMealsServed,
} from "@models/program";
import { SimilarProgram } from "@models/common";

@Component({
    templateUrl: "./program.component.html"
})

export class ProgramComponent implements OnInit, OnDestroy {
    public program: Program;
    public programId: number;
    public subscription: Subscription;
    public contactInfoValidSub: Subscription;
    public submissionValidSub: Subscription;
    public previousUrl: string;
    public currentTab: number;
    public initialized: boolean = false;
    public creating: boolean = false;
    public blockUi = false;
    public similarModalDisplay: boolean = false;
    public deleteConfirmDialog: boolean = false;
    public display: boolean = false;
    public display2: boolean = false;
    public similarPrograms: SimilarProgram[];
    public spinnerClass = "hideSpinner";
    public msgs: Message[] = [];

    contactInfoValid: boolean = false;
    submissionValid: boolean = false;

    constructor(private location: Location,
        private router: Router,
        private route: ActivatedRoute,
        private editorService: ProgramEditorService,
        private programService: ProgramService,
        private messageService: MessageService,
        private typeService: TypesService
    ) { }

    ngOnInit(): void {
        this.loading();
        this.editorService.dataReady = false;
        this.editorService.agencies = undefined;
        this.getAgencies();
        this.getCities();
        let id = this.route.snapshot.params["id"];
        if (id === undefined) {
            this.creating = true;
            this.createFreshProgram();
            this.subscribeToProgram();
            this.subscribeToContactInfoValidity();
            this.subscribeToSubmissionValidity();
            this.previousUrl = this.editorService.previousUrl;
            this.changeTab(1);
            this.finshedLoading();
        } else {
            this.programId = +id;
            this.programService.getProgram(this.programId).subscribe(data => {
                this.subscribeToProgram();
                this.subscribeToContactInfoValidity();
                this.subscribeToSubmissionValidity();
                this.editorService.updateProgram(data);
                this.previousUrl = this.editorService.previousUrl;
                this.changeTab(1);
                this.finshedLoading();
            });
        }
    }

    getCities(): void {
        if (this.editorService.cities == undefined) {
            this.typeService.getCities().subscribe(
                response => {
                    this.editorService.cities = response;
                    this.editorService.completeCitiesFetch();
                }
            );
        }
    }

    getAgencies(): void {
        this.typeService.getAgencies().subscribe(
            response => {
                this.editorService.agencies = response;
                this.editorService.completeAgenciesFetch();
            }
        );
    }

    ngOnDestroy(): void {
        if (this.subscription != undefined) {
            this.subscription.unsubscribe();
        }
        if (this.contactInfoValidSub != undefined) {
            this.contactInfoValidSub.unsubscribe();
        }
        if (this.submissionValidSub != undefined) {
            this.submissionValidSub.unsubscribe();
        }
    }

    subscribeToSubmissionValidity(): void {
        this.editorService.submissionValid$.subscribe(x => {
            this.submissionValid = x;
        });
    }

    subscribeToContactInfoValidity(): void {
        this.editorService.contactInfoValid$.subscribe(x => this.contactInfoValid = x);
    }

    subscribeToProgram(): void {
        this.subscription = this.editorService.program$.subscribe(x => this.program = x);
    }

    moveToTab(tab: number): void {
        tab = +tab;
        if (!this.contactInfoValid) {
            this.editorService.updateShowContactInfoErrors(true);
            this.msgs = [];
            this.msgs.push({ severity: "error", summary: "Validation", detail: "Please check contact information" });
            console.log("should show warning growl");
            this.changeTab(1);
            return;
        } else {
            this.editorService.updateShowContactInfoErrors(false);
        }

        this.changeTab(tab);
    }

    changeTab(tab: number) {
        if (this.currentTab === tab) {
            // do nothing
            return;
        }
        let link = "/program/";
        this.currentTab = tab;
        if (!this.creating) { link = "/program/" + this.programId; }
        switch (tab) {
            case 1:
                link += "/contact-info";
                break;
            case 2:
                link += "/details";
                break;
            case 3:
                link += "/services";
                break;
        }
        this.router.navigate([link]);
    }

    cancel(): void {
        this.router.navigate([this.previousUrl]);
    }

    loading(): void {
        this.blockUi = true;
        this.spinnerClass = "showSpinner";
    }

    finshedLoading(): void {
        this.blockUi = false;
        this.spinnerClass = "hideSpinner";
    }

    save(confirm: boolean): void {
        if (!this.contactInfoValid) {
            this.editorService.updateShowContactInfoErrors(true);
            this.msgs = [];
            this.msgs.push({ severity: "error", summary: "Validation", detail: "Please check contact information" });
            this.changeTab(1);
        } else {
            this.loading();
            if (confirm) { this.program.similarConfirmed = true; }
            this.editorService.updateShowContactInfoErrors(false);
            this.editorService.updateShowSubmitErrors(false);
            this.similarModalDisplay = false;
            if (this.creating) {
                this.programService.createProgram(this.program).subscribe(
                    response => {
                        this.finshedLoading();
                        if (!response.success && response.similarPrograms) {
                            this.similarPrograms = response.similarPrograms;
                            this.similarModalDisplay = true;
                        } else {
                            this.messageService.add({ severity: "success", summary: "Success", detail: "New program added" });
                            let link = "/program/" + response.id;
                            this.router.navigate([link]);
                            this.getAgencies();
                        }
                    },
                    error => {
                        this.finshedLoading();
                        this.msgs = [];
                        this.msgs.push({ severity: "error", summary: "Error", detail: "There was a problem adding the new program" });
                        console.log(error);
                    }
                );
            } else {
                this.programService.saveProgram(this.program).subscribe(
                    response => {
                        this.finshedLoading();
                        if (!response.success && response.similarPrograms) {
                            this.similarPrograms = response.similarPrograms;
                            this.similarModalDisplay = true;
                        } else {
                            this.msgs = [];
                            this.msgs.push({ severity: "success", summary: "Success", detail: "Program saved successfully" });
                            this.getAgencies();
                        }
                        console.log(response);
                    },
                    error => {
                        this.finshedLoading();
                        this.msgs = [];
                        this.msgs.push({ severity: "error", summary: "Error", detail: "There was a problem saving the program" });

                        console.log(error);
                    }
                );
            }
        }
    }

    createFreshProgram(): void {
        let program = new Program();
        let contactInfo = new ProgramContactInfo();
        let details = new ProgramDetails();
        let funding = new ProgramFunding();
        let services = new ProgramServices();
        let timing = new ProgramTimings();
        let grades = new ProgramGradesServed();
        let offered = new ProgramProgramsOffered();
        let meals = new ProgramMealsServed();

        program.contactInfo = contactInfo;
        details.funding = funding;
        program.details = details;
        services.timing = timing;
        services.gradesServed = grades;
        services.programsOffered = offered;
        services.mealsServed = meals;
        program.services = services;

        this.editorService.updateProgram(program);
    }

    getName(): string {
        return this.program ? this.program.contactInfo.name : "";
    }

    getCreatedDate(): string {
        return this.program ? this.program.displayCreatedDate : "";
    }

    getModalHeader(): string {
        let header = "";
        if (this.deleteConfirmDialog) { header = "Delete Program Confirmation"; }
        if (this.similarModalDisplay) { header = "Similar Programs Found"; }
        return header;

    }

    navigateToProgram(id: number = this.program.id) {
        this.loading();
        this.router.navigate(["/program/" + id]);
        this.programService.getProgram(id).subscribe(data => {
            this.currentTab = undefined;
            this.changeTab(1);
            this.editorService.updateProgram(data);
            this.similarModalDisplay = false;
            this.finshedLoading();
        });
    }

    deleteProgram(): void {
        this.loading();
        this.programService.deleteProgram(this.program.id).subscribe(
            response => {
                this.finshedLoading();
                this.messageService.add({ severity: "success", summary: "Success", detail: "Program deleted" });
                this.router.navigate(["/programs/"]);
            },
            error => {
                this.finshedLoading();
                this.msgs = [];
                this.msgs.push({ severity: "error", summary: "Error", detail: "There was a problem deleting the program" });

                console.log(error);
            });

    }

}




// WEBPACK FOOTER //
// C:/WORK/Projects/ActNow_NG2/src/app/programs/program/program.component.ts